import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { ROUTES } from "@/app/routes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TableSkeleton } from "@/components/shared/loading-skeleton";
import {
  useGetApiIdentityRulesChannelUserId,
  usePostApiIdentityRules,
} from "@/lib/api/generated/identity/identity";
import { postApiIdentityUsers } from "@/lib/api/generated/identity/identity";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { queryKeys } from "@/lib/query/keys";
import { toastMutationError } from "@/lib/utils/formatting";
import type { User } from "@/lib/api/generated/control-api.schemas";

const editUserSchema = z.object({
  userId: z.string().min(1, "User is required"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().min(1, "Email is required").email("Must be a valid email address"),
  phone: z.string().min(1, "Phone is required"),
});

type EditUserForm = z.infer<typeof editUserSchema>;

export function EditIdentityRulePage() {
  const { channel, userId } = useParams<{
    channel: string;
    userId: string;
  }>();
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const {
    data: rule,
    isLoading,
    error,
  } = useGetApiIdentityRulesChannelUserId(
    channel ?? "",
    userId ?? "",
    {
      query: { enabled: Boolean(tenantId && channel && userId) },
    },
  );
  const { data: directoryUsers, error: directoryUsersError } = useQuery({
    queryKey: queryKeys.tenantDirectoryUsers(tenantId ?? ""),
    queryFn: async () =>
      postApiIdentityUsers({
        tenantId: tenantId ?? "",
        page: 1,
        pageSize: 100,
      }),
    enabled: Boolean(tenantId),
  });
  const mutation = usePostApiIdentityRules();
  useDocumentTitle("Edit Identity Mapping");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditUserForm>({
    resolver: zodResolver(editUserSchema),
  });

  useEffect(() => {
    if (rule) {
      reset({
        userId: rule.userId ?? "",
        firstName: rule.firstName ?? "",
        lastName: rule.lastName ?? "",
        email: rule.email ?? "",
        phone: rule.phone ?? "",
      });
    }
  }, [rule, reset]);

  const onSubmit = async (data: EditUserForm) => {
    if (!rule) {return;}

    const displayName = `${data.firstName} ${data.lastName}`;

    try {
      await mutation.mutateAsync({
        data: {
          tenantId: tenantId!,
          channel: rule.channel,
          userId: data.userId,
          displayName,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          metadata: rule.metadata,
        },
      });
      toast.success("Identity mapping updated");
      navigate(ROUTES.tenantIdentity(tenantId!));
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to update identity mapping"));
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-xl mx-auto">
        <TableSkeleton rows={4} cols={1} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto">
        <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
          {error instanceof Error ? error.message : "Failed to load identity mapping."}
        </p>
      </div>
    );
  }

  if (!rule) {
    return (
      <div className="max-w-xl mx-auto">
        <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
          Identity mapping not found.
        </p>
      </div>
    );
  }

  const selectedUser = (directoryUsers?.items ?? []).find(
    (user) => user.id === rule.userId,
  );

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        Edit Identity Mapping
      </h1>
      <Card>
        <CardHeader>
          <CardTitle>Mapping Details</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Read-only identity fields */}
          <div className="mb-6 flex flex-col gap-3">
            <ReadOnlyField
              label="User"
              value={selectedUser ? formatUserOption(selectedUser) : rule.userId}
            />
            <ReadOnlyField label="User ID" value={rule.userId} mono />
            <ReadOnlyField label="Channel" value={rule.channel} />
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField label="User" error={errors.userId?.message}>
              <select {...register("userId")} className="form-input">
                <option value="">Select user</option>
                {(directoryUsers?.items ?? []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {formatUserOption(user)}
                  </option>
                ))}
              </select>
              {directoryUsersError instanceof Error ? (
                <span className="text-[11px] mt-1" style={{ color: "var(--danger)" }}>
                  {directoryUsersError.message}
                </span>
              ) : null}
            </FormField>

            <FormField label="First Name" error={errors.firstName?.message}>
              <input
                {...register("firstName")}
                placeholder="Alice"
                className="form-input"
              />
            </FormField>

            <FormField label="Last Name" error={errors.lastName?.message}>
              <input
                {...register("lastName")}
                placeholder="Example"
                className="form-input"
              />
            </FormField>

            <FormField label="Email" error={errors.email?.message}>
              <input
                {...register("email")}
                type="email"
                placeholder="alice@example.com"
                className="form-input"
              />
            </FormField>

            <FormField label="Phone" error={errors.phone?.message}>
              <input
                {...register("phone")}
                placeholder="+15550001234"
                className="form-input"
              />
            </FormField>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Mapping"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate(ROUTES.tenantIdentity(tenantId!))}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function formatUserOption(user: User): string {
  const parts = [user.displayName];
  if (user.email) {
    parts.push(user.email);
  }
  parts.push(user.id);
  return parts.join(" · ");
}

function ReadOnlyField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </span>
      <p
        className={`text-xs mt-1 ${mono ? "font-mono" : ""}`}
        style={{ color: "var(--muted-foreground)" }}
      >
        {value}
      </p>
    </div>
  );
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {error && (
        <span className="text-xs" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      )}
    </div>
  );
}
